/**
 * HustleEventsSalesPage — hustleevents.au front door.
 *
 * Marketing page for the HustleEvents ticketing platform. Sits on the
 * bare `hustleevents.au` domain; the software itself lives on
 * `app.hustleevents.au` (routed by `RootRouter.jsx`).
 *
 * i18n: every visible string goes through `useTranslation()` so the
 * 40-language GoogleTranslateBar renders it correctly. Global header
 * + footer are the platform-wide `PlatformHeader` + `SuiteFooter`
 * snippets — no bespoke chrome, per the "module name as token" rule.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Ticket, Sparkles, ShieldCheck, ArrowRight,
  Wallet, Calendar as CalendarIcon, LayoutGrid,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const APP = "https://app.hustleevents.au";

export default function HustleEventsSalesPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "HustleEvents — Sell tickets. Pick seats. Wallet-ready.";
  }, [i18n.language]);

  const features = [
    ["events_feat_seats_t",  "events_feat_seats_b",  LayoutGrid,
      "Every seating model", "General admission, sections × rows × seats, or round-table banquets with menus — all in one builder."],
    ["events_feat_wallet_t", "events_feat_wallet_b", Wallet,
      "Wallet-native", "Every ticket auto-installs to Apple Wallet + Google Wallet with a scannable QR and geo-triggered lockscreen popup."],
    ["events_feat_cal_t",    "events_feat_cal_b",    CalendarIcon,
      "Calendar in one tap", "Guests add the event to Google, Outlook or iCloud with a single tap — powered by the universal /ics endpoint."],
    ["events_feat_trust_t",  "events_feat_trust_b",  ShieldCheck,
      "Total transparency", "Every dollar traceable — buyer, vendor and HustleSuite each see the same ledger row for every charge, transfer and refund."],
  ];

  return (
    <div
      data-testid="hustleevents-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustleEvents", to: "/" }}
        nav={[
          { to: "/", label: t("events_nav_home", { defaultValue: "Home" }) },
          { to: "/hustle-events", label: t("events_nav_catalog", { defaultValue: "Events" }) },
        ]}
        ctaLabel={t("events_cta_launch", { defaultValue: "Launch app" })}
        ctaLink={APP}
        showBookCta
        showCart={false}
      />

      <header className="max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
        <div className="mx-auto w-20 h-20 rounded-2xl bg-amber-400/10 flex items-center justify-center">
          <Ticket size={36} className="text-amber-400" />
        </div>
        <p className="mt-6 text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
          {t("events_hero_kicker", { defaultValue: "HustleEvents" })}
        </p>
        <h1 className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl mx-auto">
          {t("events_hero_title", {
            defaultValue: "Sell tickets. Pick seats. Wallet-ready.",
          })}
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-stone-500 dark:text-stone-300 leading-relaxed">
          {t("events_hero_sub", {
            defaultValue:
              "General admission, sections and rows, or round-table banquets with menus — HustleEvents ticks every seating model. Every ticket lands in Apple Wallet + Google Wallet with a scannable QR, an ICS calendar file, and a geo-triggered lockscreen popup at the venue.",
          })}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={APP}
            data-testid="events-cta-primary"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-black text-slate-950 bg-amber-400 hover:bg-amber-300 shadow-[0_0_60px_-15px_#fbbf24aa]"
          >
            <Sparkles size={16} />
            {t("events_cta_start", { defaultValue: "Start selling tickets" })}
          </a>
          <Link
            to="/hustle-events"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-semibold border border-stone-300 dark:border-stone-700 hover:border-amber-400 hover:text-amber-500"
          >
            {t("events_cta_browse", { defaultValue: "Browse upcoming events" })}
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-stone-400 dark:text-stone-500">
          <ShieldCheck size={12} className="text-emerald-500" />
          {t("events_hero_trust", {
            defaultValue: "Marketplace ledger — every dollar traceable from buyer to vendor to payout.",
          })}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-3xl font-black text-center mb-10">
          {t("events_feat_title", { defaultValue: "Everything you need on one ticket" })}
        </h2>
        <div
          data-testid="events-features"
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {features.map(([tk, bk, Icon, defT, defB]) => (
            <div
              key={tk}
              className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white/60 dark:bg-slate-950/40 p-6"
            >
              <Icon size={20} className="text-amber-500" />
              <h3 className="mt-4 font-bold">{t(tk, { defaultValue: defT })}</h3>
              <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">{t(bk, { defaultValue: defB })}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-8">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.28em]">
            {t("events_cta_kicker", { defaultValue: "Free for Founder Members" })}
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-black">
            {t("events_cta_title", {
              defaultValue: "Launch your first event in under 10 minutes.",
            })}
          </h2>
          <p className="mt-4 text-stone-500 dark:text-stone-300">
            {t("events_cta_sub", {
              defaultValue:
                "HustleEvents is bundled with every HustleSuite Pro membership. Stripe payouts, wallet passes, calendar invites and refunds are all baked in.",
            })}
          </p>
          <a
            href={APP}
            className="mt-6 inline-flex items-center gap-2 h-12 px-8 rounded-full text-base font-black text-slate-950 bg-amber-400 hover:bg-amber-300"
          >
            {t("events_cta_open_app", { defaultValue: "Open the app" })}
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <SuiteFooter trivagoContext="events" />
    </div>
  );
}
